import { type RoomReservationSummary } from '@exam-center/exam-location-management';
import {
  Button,
  DataGrid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  OutgoingMailOutlinedIcon,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs } from '@shared/utils';

import { useSendRetireNotificationEmail } from '../../hooks/use-send-retire-notification-email';
interface CannotRetireExamCenterRoomModalProps {
  open: boolean;
  onClose: () => void;
  reservations: RoomReservationSummary[];
}

const columns = [
  {
    field: 'examCenterCode',
    headerName: 'ศูนย์ทดสอบ',
    flex: 1,
    sortable: false,
  },
  {
    field: 'eventCount',
    headerName: 'จำนวนรอบสอบ',
    width: 180,
    sortable: false,
    renderCell: (params: { row: RoomReservationSummary }) => (
      <Text>{params.row.eventCount} รอบ</Text>
    ),
  },
  {
    field: 'usageDateRange',
    headerName: 'ช่วงวันที่ใช้งาน',
    width: 220,
    sortable: false,
    renderCell: (params: { row: RoomReservationSummary }) => {
      const { startDate, endDate } = params.row;
      const formattedStartDate = dayjs(startDate).format('DD/MM/YYYY');
      const formattedEndDate = dayjs(endDate).format('DD/MM/YYYY');
      return (
        <Text>
          {formattedStartDate} - {formattedEndDate}
        </Text>
      );
    },
  },
];

export default function CannotRetireExamCenterRoomModal(
  props: Readonly<CannotRetireExamCenterRoomModalProps>,
) {
  const { open: isOpen, onClose, reservations } = props;

  const { showSnackbar } = useSnackbar();
  const [sendEmail, { loading: isSendingEmail }] = useSendRetireNotificationEmail();

  const handleSendEmail = async () => {
    await sendEmail();
    showSnackbar({ message: 'ส่งอีเมลแจ้งศูนย์ทดสอบเรียบร้อยแล้ว', variant: 'success' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle onClose={onClose}>ไม่สามารถยกเลิกการใช้งานได้</DialogTitle>
      <DialogContent dividers>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          <Text>
            ระบบตรวจพบว่า มีรอบสอบที่ยังใช้งานสถานที่นี้อยู่ กรุณาแจ้งศูนย์ทดสอบที่เกี่ยวข้อง
            ให้แก้ไขสถานที่ออกจากทุกรอบสอบทั้งหมดก่อน
          </Text>

          <DataGrid
            columns={columns}
            rows={reservations.map((r, i) => ({ ...r, id: `reservation-${i}` }))}
            columnHeaderHeight={48}
            rowHeight={52}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={(row) => (row as RoomReservationSummary & { id: string }).id}
            sx={(theme) => ({
              border: '1px solid',
              borderColor: theme.palette.grey[200],
              borderRadius: theme.shape.borderRadius,
              '.MuiDataGrid-columnHeaders': {
                bgcolor: theme.palette.grey[100],
                borderBottom: '1px solid',
                borderColor: theme.palette.grey[200],
              },
              '.MuiDataGrid-columnHeader': {
                px: theme.spacing(3),
              },
              '.MuiDataGrid-row .MuiDataGrid-cell': {
                p: theme.spacing(3),
              },
              '.MuiDataGrid-cell, .MuiDataGrid-row': {
                borderBottom: '1px solid',
                borderColor: theme.palette.grey[200],
              },
            })}
          />

          <Text>ระบบจะอนุญาตให้ยกเลิกได้ เมื่อไม่มีรอบสอบใดใช้งานสถานที่นี้อยู่แล้ว</Text>
        </Flex>
      </DialogContent>

      <Divider />
      <DialogActions
        sx={(theme) => ({
          justifyContent: 'space-between',
          p: theme.spacing(5, 6, 6, 6),
        })}
      >
        <Button
          size='large'
          variant='outlined'
          sx={{
            visibility: 'hidden',
          }}
          color='secondary'
          onClick={handleSendEmail}
          loading={isSendingEmail}
          startIcon={<OutgoingMailOutlinedIcon />}
        >
          ส่งอีเมลแจ้งศูนย์ทดสอบให้แก้ไข
        </Button>
        <Button size='large' color='primary' onClick={onClose} disabled={isSendingEmail}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
